import { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { TAGLINE } from '@slipway/shared';
import { Mark } from './Icons.js';

export interface PairScreenProps {
  code: string | null;
  error: string | null;
  onRetry: () => void;
}

export function PairScreen({ code, error, onRetry }: PairScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!code || !canvasRef.current) return;
    // Standard polarity — dark modules on a light field. An inverted QR scans
    // slowly and unreliably on iOS Camera, and onboarding is the one place we
    // cannot afford a fiddly moment. The bright card also gives the dark
    // screen a deliberate focal point.
    void QRCode.toCanvas(canvasRef.current, `slipway://pair/${code}`, {
      width: 196,
      margin: 1,
      color: { dark: '#0A0B0D', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    });
  }, [code]);

  return (
    <div className="pair">
      <Mark className="logo" />

      <div>
        <h1>Slipway</h1>
        <p className="tagline">{TAGLINE}</p>
      </div>

      {error ? (
        <>
          <p className="pair-status" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
          <button className="send" onClick={onRetry}>
            Try again
          </button>
        </>
      ) : (
        <>
          <div className="qr-frame">
            <canvas ref={canvasRef} width={188} height={188} data-testid="pair-qr" />
          </div>

          {code && (
            <div className="paircode" data-testid="pair-code">
              {code.slice(0, 5)} {code.slice(5)}
            </div>
          )}

          <ol className="pair-steps">
            <li>
              <span className="n">01</span>
              <span>Open Slipway on your iPhone</span>
            </li>
            <li>
              <span className="n">02</span>
              <span>Tap Devices, then Add a computer</span>
            </li>
            <li>
              <span className="n">03</span>
              <span>Point it at this code</span>
            </li>
          </ol>

          <div className="pair-status">
            <span className="lamp" data-state="connecting" />
            <span>Waiting for your phone…</span>
          </div>
        </>
      )}
    </div>
  );
}
